import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RootLayout } from './components/layout/RootLayout';
import { VariantHomePage, VariantOwnersPage, VariantProvidersPage } from './pages/variants';
import { PrivacyPage, TermsPage } from './pages/LegalPage';
import { ThankYouOwnerPage, ThankYouProviderPage } from './pages/ThankYouPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { NotFoundPage } from './pages/NotFoundPage';

/**
 * Admin is a separate bundle, fetched only when someone opens /admin.
 *
 * Every visitor to the public site downloads the main chunk; almost none of
 * them are the operator. Splitting here keeps the dashboard's tables, filters,
 * and formatting out of the page a horse owner loads on barn wifi (spec
 * section 18, performance budget).
 */
const AdminApp = lazy(() => import('./admin/AdminApp'));

/**
 * The public route table (spec section 05).
 *
 * Exported without a router so tests can mount it inside a MemoryRouter. The
 * browser router itself lives in App.
 *
 * /admin sits outside RootLayout: it is an internal tool, not a page of the
 * site, and it must not carry the public header, footer, or navigation. Access
 * control lives on the server — the route below renders a login screen, not a
 * secret (see src/server/routes/admin). /health is served by Express and never
 * reaches the client.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={null}>
            <AdminApp />
          </Suspense>
        }
      />
      <Route element={<RootLayout />}>
        {/* Three routes, two designs each. The paths are unchanged: the
            variant gate picks a rendering, not a URL (see pages/variants). */}
        <Route path="/" element={<VariantHomePage />} />
        <Route path="/owners" element={<VariantOwnersPage />} />
        <Route path="/providers" element={<VariantProvidersPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/thank-you/owner" element={<ThankYouOwnerPage />} />
        <Route path="/thank-you/provider" element={<ThankYouProviderPage />} />
        {/* Reached from a link in an email. Public, unauthenticated, noindex
            (spec section 17). */}
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
